import React, {useEffect, useRef, useState} from 'react';
import {ServiceResource, useAuth} from '../hooks/use-auth';
import {Notification} from '../navigation/page-layout';
import {useRetrieveInboundBridgeBucketLevelAgg, useRetrieveInboundBridgeData} from '../hooks/use-analytics-api';
import {CommonBridgeDataRequest, useInboundBridgeFilterData} from '../hooks/use-inbound-bridge-filter-data';
import {translateErrorToReactNode} from '../common';
import {Box, ContentLayout, Header, SpaceBetween, Spinner} from '@amzn/awsui-components-react';
import {BridgeType, InboundBridgeBucketRecord, InboundBridgeRecord} from './index';
import {InboundBridgeTable} from './inbound-bridge-table';
import {InboundBridgeFilters} from './inbound-bridge-filters';
import {InboundBridgeStackedBarChart} from './inbound-bridge-stacked-bar-chart';

const COMPONENT_TEXTS = {
    page_description: 'Plan to actuals bridging',
} as const;

export function InboundBridgeP2ANode(props: {pushNotification: (notification: Notification) => void}) {
    function createErrorListener<T>(header: string) {
        return (e: any) => {
            props.pushNotification({
                type: 'error',
                content: translateErrorToReactNode(e),
                header,
            });
        };
    }

    const auth = useAuth();
    const [bridgeData, setBridgeData] = useState<InboundBridgeRecord[]>([]);
    const [bridgeBucketLevelAgg, setBridgeBucketLevelAgg] = useState<InboundBridgeBucketRecord[]>([]);
    const isLoadingData = useRef<boolean>(true);
    const isLoadingBucketLevelAgg = useRef<boolean>(true);

    const tenant = {
        business: auth.authInformation!.current!.businessId,
        country: auth.authInformation!.current!.country,
    };

    const clientConfiguration = auth.authInformation!.getCurrentServiceEndpoint(ServiceResource.ExplainabilityView);
    const {
        execute: executeRetrieveInboundBridgeData,
        status: inboundBridgeDataStatus,
        value: getInboundBridgeDataResponse,
    } = useRetrieveInboundBridgeData(clientConfiguration, createErrorListener('RetrieveInboundBridgeData failed'), [auth]);
    const {
        execute: executeRetrieveInboundBridgeBucketLevelAgg,
        status: inboundBridgeBucketLevelAggStatus,
        value: getInboundBridgeBucketLevelAggResponse,
    } = useRetrieveInboundBridgeBucketLevelAgg(
        clientConfiguration,
        createErrorListener('RetrieveInboundBridgeBucketLevelAgg failed'),
        [auth]
    );

    const {nodeFilterDataMap, selectedFilter, setSelectedFilter, isLoadingFilterData} = useInboundBridgeFilterData(
        BridgeType.P2A,
        tenant,
        false,
        clientConfiguration,
        createErrorListener,
        (params: CommonBridgeDataRequest) => {
            executeRetrieveInboundBridgeData({
                ...tenant,
                forecastType: 'sop',
                bridgeType: BridgeType.P2A,
                filter: JSON.stringify({
                    nodes: params.nodes,
                    balanceDateStart: params.balanceDateStart,
                    balanceDateEnd: params.balanceDateEnd,
                    fromHorizonInWeeks: params.fromHorizonInWeeks,
                    toHorizonInWeeks: params.toHorizonInWeeks,
                }),
            });
            executeRetrieveInboundBridgeBucketLevelAgg({
                ...tenant,
                forecastType: 'sop',
                bridgeType: BridgeType.P2A,
                filter: JSON.stringify({
                    nodes: params.nodes,
                    balanceDateStart: params.balanceDateStart,
                    balanceDateEnd: params.balanceDateEnd,
                    fromHorizonInWeeks: params.fromHorizonInWeeks,
                    toHorizonInWeeks: params.toHorizonInWeeks,
                }),
                timeGranularity: 'DAY',
                metrics: JSON.stringify(['wapeContribRescaled']),
            });
        }
    );

    isLoadingData.current = ['idle', 'pending'].includes(inboundBridgeDataStatus);
    isLoadingBucketLevelAgg.current = ['idle', 'pending'].includes(inboundBridgeBucketLevelAggStatus);

    useEffect(() => {
        if (!isLoadingData.current && getInboundBridgeDataResponse?.records) {
            const newData: InboundBridgeRecord[] = getInboundBridgeDataResponse.records.map(
                (r) =>
                    ({
                        date: r.balanceDate.split('T')[0],
                        node: r.node,
                        granularity: r.granularity,
                        bucketSeqId: r.bucketSeqId,
                        bucketName: r.bucketName,
                        bucketValue: r.bucketValue,
                        bucketError: r.bucketError,
                        lastBucketValue: r.lastBucketValue,
                        bucketWape:
                            r.lastBucketValue === 0 ? '0%' : `${Math.round((Math.abs(r.bucketError) / r.lastBucketValue) * 100)}%`,
                    } as InboundBridgeRecord)
            );

            setBridgeData(newData);
        }
    }, [isLoadingData.current]);

    useEffect(() => {
        if (!isLoadingBucketLevelAgg.current && getInboundBridgeBucketLevelAggResponse?.records) {
            const newData: InboundBridgeBucketRecord[] = getInboundBridgeBucketLevelAggResponse.records.map(
                (r) =>
                    ({
                        date: r.balanceDate.split('T')[0],
                        seqId: r.seqId,
                        name: r.name,
                        wapeContribRescaled: r.wapeContribRescaled,
                    } as InboundBridgeBucketRecord)
            );

            setBridgeBucketLevelAgg(newData);
        }
    }, [isLoadingBucketLevelAgg.current]);

    return (
        <ContentLayout defaultPadding>
            <SpaceBetween direction="vertical" size="xxl">
                <Header variant="h2" description={COMPONENT_TEXTS.page_description}>
                    Warehouse/Store-Level
                </Header>
                <InboundBridgeFilters
                    bridgeType={BridgeType.P2A}
                    tenant={tenant}
                    nodeFilterDataMap={nodeFilterDataMap}
                    selectedFilter={selectedFilter}
                    setSelectedFilter={setSelectedFilter}
                    isLoadingFilterData={isLoadingFilterData}
                    isLoadingData={isLoadingData.current}
                    onApply={() => {
                        if (selectedFilter) {
                            isLoadingData.current = true;
                            executeRetrieveInboundBridgeData({
                                ...tenant,
                                forecastType: 'sop',
                                bridgeType: BridgeType.P2A,
                                filter: JSON.stringify({
                                    nodes: [selectedFilter.node],
                                    balanceDateStart: selectedFilter.balanceDateStart,
                                    balanceDateEnd: selectedFilter.balanceDateEnd,
                                    fromHorizonInWeeks: selectedFilter.fromHorizonInWeeks,
                                    toHorizonInWeeks: selectedFilter.toHorizonInWeeks,
                                }),
                            });

                            isLoadingBucketLevelAgg.current = true;
                            executeRetrieveInboundBridgeBucketLevelAgg({
                                ...tenant,
                                forecastType: 'sop',
                                bridgeType: BridgeType.P2A,
                                filter: JSON.stringify({
                                    nodes: [selectedFilter.node],
                                    balanceDateStart: selectedFilter.balanceDateStart,
                                    balanceDateEnd: selectedFilter.balanceDateEnd,
                                    fromHorizonInWeeks: selectedFilter.fromHorizonInWeeks,
                                    toHorizonInWeeks: selectedFilter.toHorizonInWeeks,
                                    channels: selectedFilter.channels,
                                    sourceLocations: selectedFilter.sourceLocations,
                                    ibTypes: selectedFilter.ibTypes,
                                    tempZones: selectedFilter.tempZones,
                                }),
                                timeGranularity: 'DAY',
                                metrics: JSON.stringify(['wapeContribRescaled']),
                            });
                        }
                    }}
                />
                {renderVisualizations()}
            </SpaceBetween>
        </ContentLayout>
    );

    function renderVisualizations(): React.ReactElement {
        if (isLoadingFilterData) {
            return (
                <Box textAlign="center" color="inherit">
                    <Spinner size="large" />
                </Box>
            );
        }

        return (
            <SpaceBetween direction="vertical" size="xxl">
                <InboundBridgeStackedBarChart data={bridgeBucketLevelAgg} isLoading={isLoadingBucketLevelAgg.current} />
                <InboundBridgeTable
                    bridgeType={BridgeType.P2A}
                    tenant={tenant}
                    bridgeData={bridgeData}
                    selectedFilter={selectedFilter}
                    isLoading={isLoadingData.current}
                />
            </SpaceBetween>
        );
    }
}
